/**
 * Los 60 componentes de AntD que el tema define.
 *
 * `buildComponents` recibe la rampa de la marca en vez de leer la de Silia:
 * eso es lo que reemplaza a `toTraxion()`, que recorría el objeto ya construido
 * y cambiaba cualquier string que coincidiera con un hex de Silia. Ese mapeo
 * operaba sobre VALORES, no sobre significados — si dos tokens distintos
 * compartían hex, movía los dos. `ink.textInverse` y `surface.bgContainer` son
 * los dos `#ffffff` y el propio archivo dice que se mantienen aparte porque
 * "a template could want them apart"; el mapeo por hex rompía justo eso.
 *
 * Ver `theme/spec.md` §10 (H5).
 */

import type { ThemeConfig } from 'antd';
import type { BrandRamp } from './primitives';
import {
  brand,
  surface,
  ink,
  line,
  semantic,
  tagExtras,
  emotion,
  kind,
  node,
  fill,
  elevation,
  typography,
  radius,
  space,
  control,
} from './primitives';

export const buildComponents = (accent: BrandRamp) =>
  ({
    Button: {
      /** Text on the lime CTA is brand black. White on lime is unreadable. */
      colorTextLightSolid: accent.black,
      controlHeight: control.height,
      controlHeightLG: control.height,
      /** Toolbar height, reachable via `size="small"`. */
      controlHeightSM: control.heightSM,
      fontWeight: 600,
      borderRadius: radius.md,
      /** The CTA carries its own glow (`--silia-shadow-cta`); AntD's default
          drop shadow would stack a second one underneath it. */
      primaryShadow: 'none',
      defaultShadow: 'none',
      defaultHoverBg: accent.n200,
      defaultHoverColor: accent.n700,
      defaultHoverBorderColor: accent.n500,
      defaultActiveBg: accent.n200,
      defaultActiveColor: accent.n700,
      defaultActiveBorderColor: accent.n700,
    },
    /** Without `activeShadow` a focused field shows nothing: the theme matches
        the active border to the resting one and AntD draws no ring by default. */
    Input: {
      controlHeight: control.height,
      borderRadius: radius.md,
      colorBgContainer: surface.bgContainer,
      activeBorderColor: accent.n500,
      hoverBorderColor: accent.n400,
      activeShadow: `0 0 0 3px ${accent.n200}`,
    },
    /** `size="large"` must not grow a Select: AntD derives `controlHeightLG` as
      `controlHeight × 1.25` when it is not given, so a large Select resolved to
      50px against a 40px Button beside it. Button already pins both. */
    Select: {
      controlHeight: control.height,
      controlHeightLG: control.height,
      controlHeightSM: control.heightSM,
      borderRadius: radius.md,
      activeBorderColor: accent.n500,
      hoverBorderColor: accent.n400,
      activeOutlineColor: accent.n200,
    },
    DatePicker: {
      controlHeight: control.height,
      borderRadius: radius.md,
      colorPrimary: accent.n500,
      activeBorderColor: accent.n500,
      hoverBorderColor: accent.n400,
      activeShadow: `0 0 0 3px ${accent.n200}`,
      cellActiveWithRangeBg: accent.n200,
    },
    Card: {
      borderRadiusLG: radius.md,
      paddingLG: space.lg,
      colorBgContainer: surface.bgContainer,
    },
    /* Floating surfaces — modals, popovers, dropdown menus — sit on the
       elevated off-white rather than plain white. Set per component instead of
       on the global `colorBgElevated`, which AntD also resolves for tooltips,
       select dropdowns, date pickers and notifications: those keep their own
       background. */
    Modal: {
      borderRadiusLG: radius.lg,
      contentBg: surface.bgElevated,
      headerBg: surface.bgElevated,
      footerBg: surface.bgElevated,
    },
    Popover: { colorBgElevated: surface.bgElevated },
    /**
     * The action menu behind every "…" button. It renders a Menu internally, so
     * the row geometry and the danger colours are Menu tokens.
     *
     * 36px, not the 44 the home card was using: at 44 a four-item menu carries
     * 22px of air per row against a 22px line box, which reads as four separate
     * buttons rather than one list. Twenty stylesheets had their own numbers here
     * — 44px, 8px 12px, 10px 20px, 10px 16px — so a menu looked different
     * depending on which screen opened it.
     */
    /**
     * The action menu behind every "…" button.
     *
     * The row height is not a token here — AntD derives the padding from
     * `controlHeight`:
     *
     *     paddingBlock = (controlHeight - fontSize * lineHeight) / 2
     *
     * Our controlHeight is 40 and a row's line box is 22, so that lands on 9px
     * above and below every item. Four options end up reading as four separate
     * buttons rather than one list. Scoped to 32 — a menu row is a label, not a
     * control — the same formula gives 5px, which is the list the design wants.
     *
     * Twenty stylesheets had their own numbers for this (44px min-height,
     * `8px 12px`, `10px 20px`, `10px 16px`), so the same menu looked different
     * depending on which screen opened it.
     */
    Dropdown: {
      colorBgElevated: surface.bgElevated,
      borderRadiusLG: radius.sm,
      /* Matched to the account-table menu, which is the row this design follows.
       The height comes from a 44px floor with the label centred in it, not from
       vertical padding — so paddingBlock is 0 and the 22px of air is shared
       evenly by `align-items: center` (see the rule in cssVariables). */
      paddingBlock: 0,
      controlPaddingHorizontal: space.lg,
      /* Gap between a row's icon and its label. */
      marginXS: space.xs,
      /* The shell's inner padding. Its own token, dropdownEdgeChildPadding, is not
       settable from ConfigProvider — Dropdown derives it in mergeToken as
       `dropdownEdgeChildPadding: paddingXXS`, so paddingXXS is the way in. */
      paddingXXS: space.xs,
      /**
       * The danger row's hover text. AntD paints it with `colorTextLightSolid`,
       * which this theme sets to brand black so the lime CTA gets dark text — so
       * Delete was turning BLACK on hover, not white. Same trap as Tooltip.
       * Scoped here, the lime button keeps its black text.
       */
      colorTextLightSolid: ink.textInverse,
    },
    Layout: {
      colorBgLayout: surface.bgLayout,
      bodyBg: surface.bgLayout,
      siderBg: surface.bgContainer,
    },
    /**
     * Both the sidebar and the action menu behind every "…" button.
     *
     * The row is 36px. The home card was using 44, which against a 22px line box
     * leaves 22px of air per row — four items read as four separate buttons
     * rather than one list. Twenty stylesheets carried their own numbers here
     * (44px, `8px 12px`, `10px 20px`, `10px 16px`), so the same menu looked
     * different depending on which screen opened it.
     *
     * Delete is the one destructive row: red at rest, inverted on hover. Only the
     * resting colour is a token — AntD swaps the danger colour on hover but never
     * its background, so the filled state is a rule in `cssVariables`.
     */
    Menu: {
      itemSelectedColor: accent.n700,
      itemSelectedBg: surface.bgContainer,
      itemHoverColor: ink.text,
      itemHoverBg: fill.f2,
      subMenuItemBg: 'transparent',
      itemBg: 'transparent',
      itemHeight: 40,
      itemBorderRadius: radius.sm,
      /* Space between rows, so four options read as a list rather than a block. */
      itemMarginBlock: 0,
      itemMarginInline: 0,
      /* Delete is the one destructive row: red at rest, inverted on hover. The
       dropdown inherits these. Only the resting colour is a token — AntD swaps
       the danger colour on hover but never its background, so the fill is a
       rule in `cssVariables`. */
      dangerItemColor: semantic.dangerFg,
      dangerItemHoverColor: ink.textInverse,
      dangerItemActiveBg: semantic.dangerFgStrong,
    },
    Tabs: {
      itemSelectedColor: accent.n700,
      inkBarColor: accent.n700,
      colorIcon: accent.n700,
      itemColor: ink.textDescription,
      itemHoverColor: accent.n500,
    },
    /** The three form toggles read as one family, so they share a colour.
        Switch was the only one on n500, with no note saying why, and its white
        thumb over that track measures 2.71:1 — under the 3:1 WCAG 1.4.11 asks
        of a non-text UI component. On n700 it is 7:1, the same as a checked
        Radio or Checkbox, and a switch no longer reads lighter than the
        checkbox beside it. */
    Switch: {
      colorPrimary: accent.n700,
      colorPrimaryHover: accent.n600,
      colorBgContainerDisabled: surface.tint3,
    },
    Radio: {
      colorPrimary: accent.n700,
      buttonSolidCheckedColor: accent.n700,
      dotColorDisabled: surface.tint3,
    },
    Checkbox: {
      colorPrimary: accent.n700,
      colorPrimaryHover: accent.n600,
      colorPrimaryBorder: accent.n500,
      colorWhite: ink.textInverse,
    },
    Pagination: { colorPrimary: accent.n500 },
    Progress: { defaultColor: accent.n700 },
    Spin: { colorPrimary: accent.n700 },
    Divider: { colorSplit: line.split },
    /**
     * The full switch, not just its radius. This used to be two tokens, so every
     * screen that wanted a tab row wrote the rest by hand — the same block turned
     * up in seven stylesheets, two of them byte-for-byte identical, and three
     * different mechanisms were in play (CSS, a nested ConfigProvider, and inline
     * `style`). The values below are the Data Views switch, which is the one the
     * design follows.
     *
     * The 32px row is set in `cssVariables`, not here: for Segmented,
     * `controlHeight` is an alias token rather than a component one, so setting it
     * in this block resolves to the global 40 and the row stays tall. 32 is the
     * toolbar height — a Segmented is a view switch beside a title, not a form
     * field. A screen that needs the taller one asks for `size="large"`.
     *
     * The selected item takes the brand tint rather than white — on the light
     * track a white thumb reads as a gap rather than a selection.
     */
    Segmented: {
      borderRadius: radius.sm,
      trackBg: line.borderSubtle,
      trackPadding: space.xxs,
      itemColor: ink.text,
      itemHoverColor: ink.text,
      itemHoverBg: fill.f2,
      itemActiveBg: accent.n200,
      itemSelectedBg: accent.n200,
      itemSelectedColor: accent.n700,
    },
    /** Pill, always — a tag is a label, not a block. */
    Tag: { borderRadiusSM: radius.pill },
    Typography: { colorTextDescription: ink.textDescription },
    Breadcrumb: {
      itemColor: ink.textDescription,
      lastItemColor: ink.text,
      separatorColor: ink.textIcon,
    },

    /* ── Data display ────────────────────────────────────────────────────
     Everything below was falling back to AntD's stock theme: its own grays,
     its own 6px radius, its own borders. Component counts are the number of
     files importing each from 'antd', measured across app/src. */

    /** 26 files. `headerBg` is the tint, not the border colour — the stock theme
      paints it #fafafa, which is off-scale against our tint ramp. */
    Table: {
      borderRadiusLG: radius.md,
      headerBg: line.borderSubtle,
      headerColor: ink.text,
      headerSplitColor: line.borderSubtle,
      borderColor: line.borderSubtle,
      rowHoverBg: fill.f1,
      rowSelectedBg: accent.n100,
      rowSelectedHoverBg: accent.n200,
      footerBg: surface.tint2,
      footerColor: ink.textDescription,
      cellPaddingBlock: space.sm,
      cellPaddingInline: space.md,
    },
    /** 29 files. */
    List: {
      colorText: ink.text,
      colorTextDescription: ink.textDescription,
      itemPadding: `${space.sm}px ${space.md}px`,
      headerBg: 'transparent',
      footerBg: 'transparent',
    },
    /** 23 files. Purple3 rather than the stock gray, so an initials avatar reads
      as brand chrome instead of a disabled control. */
    Avatar: {
      colorTextPlaceholder: accent.n200,
      borderRadius: radius.pill,
    },
    /** 12 files. The count badge is a status, so it takes the danger fill. */
    Badge: {
      colorError: semantic.dangerFg,
      colorErrorHover: semantic.dangerFgStrong,
      textFontSize: 12,
      textFontWeight: 600,
    },
    /** 19 files. */
    Empty: {
      colorTextDescription: ink.textDescription,
    },
    /** 5 files. */
    Statistic: {
      titleFontSize: 14,
      contentFontSize: 24,
      colorTextDescription: ink.textDescription,
    },
    /** 3 files. */
    Descriptions: {
      labelBg: surface.tint2,
      titleColor: ink.text,
      colorSplit: line.borderSubtle,
    },
    /** 47 files — the most-used component with no theme entry until now. */
    Skeleton: {
      color: surface.tint2,
      colorGradientEnd: surface.tint3,
      borderRadiusSM: radius.sm,
    },

    /* ── Feedback & overlay ─────────────────────────────────────────────── */

    /** 40 files. Dark surface, so the text is the inverse token — not
      `colorTextLightSolid`, which templates repoint to their CTA text colour
      (black under Traxión, invisible on a dark tooltip). */
    /**
     * AntD sizes the tooltip bubble as `minHeight: controlHeight` with only
     * `paddingSM / 2` of vertical padding. Our controlHeight is 40 (an input's
     * height) where AntD's default is 32, so a one-line tip measures 22 + 6 + 6 =
     * 34px of content inside a 40px floor — and since the inner box is a plain
     * block, the 6px slack all lands at the bottom and the label rides high.
     *
     * `controlHeight` is passed here as a component-scoped alias: the bubble is a
     * label, not a control, so 32 is its height. That makes the content taller
     * than the floor again, which is the state AntD's own default is in.
     */
    Tooltip: {
      colorBgSpotlight: accent.n900,
      colorTextLightSolid: ink.textInverse,
      borderRadius: radius.sm,
      controlHeight: control.heightSM,
    },
    /** 37 files. All four states come from the semantic scale, and the text is
      the `-fg-strong` step: AntD paints alert text with the fill colour, which
      measures between 2.47:1 and 3.66:1 on its own background. */
    Alert: {
      borderRadiusLG: radius.md,
      colorSuccessBg: semantic.successBg,
      colorSuccessBorder: semantic.successBd,
      colorWarningBg: semantic.warningBg,
      colorWarningBorder: semantic.warningBd,
      colorErrorBg: semantic.dangerBg,
      colorErrorBorder: semantic.dangerBd,
      // From the ramp, not `semantic`: `info` IS the accent, so a brand that
      // passes its own ramp gets its own info panel instead of inheriting
      // Silia's violet (guarded by brandLeak.test.ts).
      colorInfoBg: accent.n100,
      colorInfoBorder: accent.n400,
    },
    /** 10 files. */
    Popconfirm: {
      colorBgElevated: surface.bgElevated,
      borderRadiusLG: radius.md,
    },
    /** 4 files. Same elevated off-white as Modal and Popover. */
    Drawer: {
      colorBgElevated: surface.bgElevated,
      footerPaddingBlock: space.md,
      footerPaddingInline: space.lg,
    },
    /** 15 files (via the `notification` static). */
    Notification: {
      borderRadiusLG: radius.md,
      colorBgElevated: surface.bgElevated,
    },
    /** 88 files (via the `message` static) — the single most-called API here. */
    Message: {
      borderRadiusLG: radius.sm,
      colorBgElevated: surface.bgElevated,
    },
    /** 6 files. */
    Result: {
      // No colorInfo override: the global token is the brand ramp now. This used
      // to pin n700 locally to escape Ant Design's blue.
      titleFontSize: 20,
      subtitleFontSize: 14,
    },

    /* ── Data entry ──────────────────────────────────────────────────────── */

    /** 42 files. `labelColor` is the strong text: a form label is content, not a
      hint, and the stock theme renders it at the description weight. */
    Form: {
      labelColor: ink.text,
      labelFontSize: 14,
      /* AntD derives this from controlHeight (40 here), so every label got a 40px
       box to show a 22px line — 18px of air between a label and its field. In a
       vertical layout the label is one line, so it takes the line box. */
      labelHeight: 22,
      /* 16, not the 24 this was: that is the page-section step, and between two
       fields it reads as a gap rather than a form. Three stylesheets were
       already overriding it downward on their own. */
      itemMarginBottom: space.md,
      verticalLabelPadding: `0 0 ${space.xxs}px`,
    },
    /** 9 files. Matches Input so a number field is not a different height.
      (TimePicker needs no entry of its own — it renders through DatePicker
      and picks up those tokens.) */
    InputNumber: {
      controlHeight: control.height,
      borderRadius: radius.md,
      activeBorderColor: accent.n500,
      hoverBorderColor: accent.n400,
      activeShadow: `0 0 0 3px ${accent.n200}`,
    },
    /** 7 files. */
    Upload: {
      colorPrimary: accent.n700,
      colorBorder: line.border,
      actionsColor: ink.textIcon,
    },
    /** 2 files. */
    Slider: {
      trackBg: accent.n500,
      trackHoverBg: accent.n600,
      handleColor: accent.n700,
      handleActiveColor: accent.n800,
      railBg: line.borderSubtle,
      railHoverBg: line.border,
      dotBorderColor: line.border,
      dotActiveBorderColor: accent.n700,
    },
    /** 2 files. */
    ColorPicker: {
      borderRadius: radius.md,
    },

    /* ── Navigation & structure ──────────────────────────────────────────── */

    /** 7 files. Header transparent so a Collapse sits on whatever surface holds
      it rather than punching a gray block through the card. */
    Collapse: {
      headerBg: 'transparent',
      borderRadiusLG: radius.md,
      colorBorder: line.borderSubtle,
      contentBg: 'transparent',
      headerPadding: `${space.sm}px ${space.md}px`,
    },
    /** 1 file. */
    Steps: {
      colorPrimary: accent.n700,
      navArrowColor: ink.textIcon,
    },
    /** Not imported directly — Table, Select and Dropdown render it internally,
      so it repaints wherever pagination-like empty states appear. */
    Tree: {
      nodeHoverBg: fill.f2,
      nodeSelectedBg: accent.n200,
      directoryNodeSelectedBg: accent.n700,
    },

    /* ── Not imported today ──────────────────────────────────────────────
     The rest of AntD's themeable surface. None of these appear in an import
     in app/src right now, but they are one `import { Rate } from 'antd'`
     away, and an unthemed component is the way stock AntD gray gets back
     into the product. Defining them here costs a few lines and removes that
     path entirely. Anything with real chrome — a dot, a rail, a selected
     row — is pinned to the purple ramp; the rest to the neutral scale. */

    /** Sticky chrome: the shadow that appears once it detaches from the flow. */
    Affix: {
      zIndexPopup: 10,
    },
    Anchor: {
      linkPaddingBlock: space.xxs,
      linkPaddingInlineStart: space.md,
      colorPrimary: accent.n700,
    },
    BackTop: {
      colorText: ink.textInverse,
      colorBgElevated: accent.n700,
    },
    /** The dots are chrome, so they follow the ramp rather than AntD's white. */
    Carousel: {
      dotWidth: 16,
      dotHeight: 3,
      dotGap: space.xxs,
      dotOffset: space.sm,
      arrowSize: 16,
      arrowOffset: space.xs,
    },
    /** Selected option matches Select's, so the two dropdowns are one language. */
    Cascader: {
      optionSelectedBg: accent.n200,
      optionSelectedColor: accent.n700,
      optionSelectedFontWeight: 600,
      optionPadding: `${space.xxs}px ${space.sm}px`,
      menuPadding: space.xxs,
      borderRadius: radius.md,
    },
    /** Preview chrome sits on the scrim, so its controls are the inverse token. */
    Image: {
      previewOperationColor: ink.textInverse,
      previewOperationHoverColor: ink.textInverse,
      previewOperationColorDisabled: ink.textIcon,
    },
    Mentions: {
      controlHeight: control.height,
      borderRadius: radius.md,
      activeBorderColor: accent.n500,
      hoverBorderColor: accent.n400,
    },
    /** A rating is a value, not a warning — the stock gold reads as an alert
      next to our amber. Lime is the brand's only accent, so it takes it. */
    Rate: {
      starColor: accent.lime,
      starBg: line.border,
      starSize: 18,
    },
    Timeline: {
      tailColor: line.border,
      dotBg: surface.bgContainer,
      itemPaddingBottom: space.lg,
    },
    Transfer: {
      listHeight: 240,
      itemHeight: control.heightSM,
      headerHeight: control.height,
      borderRadiusLG: radius.md,
    },
    /** The tour bubble is a branded surface: purple ground, inverse text. */
    Tour: {
      colorPrimary: accent.n700,
      primaryPrevBtnBg: accent.n600,
      primaryNextBtnHoverBg: accent.n100,
      closeBtnSize: 16,
      borderRadiusLG: radius.md,
    },
    Splitter: {
      splitBarSize: 2,
      splitBarDraggableSize: 20,
      splitTriggerSize: 6,
      colorFill: line.border,
      colorPrimary: accent.n500,
    },
    /** Only the panel; the trigger buttons take Button's tokens. */
    FloatButton: {
      colorPrimary: accent.n700,
      colorText: ink.textInverse,
      colorFillContent: fill.f2,
      borderRadiusLG: radius.md,
    },
    Calendar: {
      colorPrimary: accent.n700,
      itemActiveBg: accent.n200,
      fullBg: surface.bgContainer,
      fullPanelBg: surface.bgContainer,
      borderRadiusLG: radius.md,
    },
    Flex: {
      colorText: ink.text,
    },

    /* Five entries AntD's type accepts but that carry no token of their own:
     Grid, Space, TreeSelect, QRCode and App. TreeSelect renders through Select
     and Tree, both defined above; the other four have no themeable surface.
     Adding stubs would be four blocks of dead config to keep in sync, so they
     are deliberately absent — see theme-silia.test.tsx, which asserts the gap
     is exactly these five and fails if AntD ever gives them tokens. */
  }) satisfies ThemeConfig['components'];

/** Los componentes con la rampa de Silia — la superficie que ya existía. */
export const antdComponents = buildComponents(brand);
